import { ReactElement, useState } from 'react'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { TableContainer, Table, Thead, Th, Tbody, Tr, TdId, IdWrapper, Td } from 'components/v2/Table'
import { FormattedMessage } from 'react-intl'
import { CopyToClipboard } from '@10xLabs/react-web/components/CopyToClipboard'
import styled from 'styled-components'
import { GTBusinessCustomer } from 'gql/types.generated'
import { BusinessCustomerListActions } from 'features/BusinessCustomer/components/BusinessCustomerList/Actions'
import { canWrite } from 'features/Users/utils'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { Checkbox } from 'components/v2/Checkbox'
import { BusinessCustomerMultipleActions } from 'features/BusinessCustomer/components/BusinessCustomerList/MultipleActions'
import Modal from 'components/v2/Modal'
import Heading from 'components/v2/Heading'
import { intToLetter } from 'utils/number'

interface IBusinessCustomerListProps {
  items: GTBusinessCustomer[]
}

const BalanceTh = styled(Th)`
  white-space: nowrap;
`
const Status = styled.div<{ isActive: boolean }>`
  text-transform: capitalize;
  font-weight: 500;
  white-space: nowrap;
  border-radius: 24px;
  color: #fff;
  width: 96px;
  height: 24px;
  text-align: center;
  font-size: 14px;
  line-height: 24px;
  background-color: ${({ isActive }): string => (isActive ? '#00C875' : '#B0B0B0')};
`

const ModalContainer = styled(Modal.Container)`
  max-width: 600px;
`
const StyledModalHeader = styled(Modal.Header)`
  align-items: start;
  margin-bottom: 0;
  & > div:first-child {
    margin-inline: auto;
  }
`
const StyledHeading = styled(Heading.H5)`
  margin-top: 24px;
`
const StyledModalContent = styled(Modal.Content)`
  text-align: center;
`

const StyledEmail = styled.span`
  color: #916ee8;
`
const SucessResetPasswordRequestModal = ({
  userAccountEmail,
  onClose
}: {
  userAccountEmail: string
  onClose: () => void
}): ReactElement => {
  return (
    <ModalContainer>
      <StyledModalHeader onClose={(): void => onClose()}>
        <StyledHeading>
          <FormattedMessage id="businessCustomer.resetPassword" defaultMessage="A link has been sent" />
        </StyledHeading>
      </StyledModalHeader>

      <StyledModalContent>
        <p>
          <FormattedMessage
            id="businessCustomer.resetPasswordSuccess"
            defaultMessage="An email was sent to {userAccountEmail} to change the password."
            values={{ userAccountEmail: <StyledEmail>{userAccountEmail}</StyledEmail> }}
          />
        </p>
      </StyledModalContent>
    </ModalContainer>
  )
}

const BusinessCustomerList = ({ items }: IBusinessCustomerListProps): ReactElement => {
  const login = useLogin()
  const canSeeActions = canWrite(FEATURES.BUSINESS_CUSTOMER, login.accessToken.policies)
  const [selectedItems, setSelectedItems] = useState<GTBusinessCustomer[]>([])
  const [userAccountForPasswordReset, setUserAccountForPasswordReset] = useState<
    GTBusinessCustomer['userAccount'] | null
  >(null)

  const handleSelectAll = (): void => {
    if (selectedItems.length === items.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(items)
    }
  }

  const handleSelectItem = (item: GTBusinessCustomer): void => {
    if (selectedItems.map(({ id }) => id).includes(item.id)) {
      setSelectedItems(selectedItems.filter((i) => i.id !== item.id))
    } else {
      setSelectedItems([...selectedItems, item])
    }
  }

  return (
    <div>
      <TableContainer overflow>
        <Table>
          <Thead>
            <Tr>
              {canSeeActions && (
                <>
                  <Th noUppercase>
                    <BusinessCustomerMultipleActions selectedItems={selectedItems} />
                  </Th>
                  <Th>
                    <Checkbox
                      id="all-trips-selected"
                      checked={selectedItems.length === items.length}
                      onChange={(): void => {
                        handleSelectAll()
                      }}
                    />
                  </Th>
                </>
              )}
              <Th>
                <FormattedMessage id="businessCustomer.id" defaultMessage="Customer Id" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.userId" defaultMessage="Account Id" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.status" defaultMessage="Status" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.name" defaultMessage="Name" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.phone" defaultMessage="Phone" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.email" defaultMessage="Email" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.client" defaultMessage="Client" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.site" defaultMessage="Site" />
              </Th>
              <Th>
                <FormattedMessage id="businessCustomer.category" defaultMessage="Category" />
              </Th>
              <BalanceTh>
                <FormattedMessage id="businessCustomer.scheduledCredits" defaultMessage="Scheduled Credits" />
              </BalanceTh>
            </Tr>
          </Thead>
          <Tbody>
            {items.map((customer) => {
              return (
                <Tr key={customer.id}>
                  {canSeeActions && (
                    <Td>
                      <BusinessCustomerListActions
                        isKreditEnabled={customer.businessClient.isKreditEnabled}
                        businessCustomer={customer}
                        disabled={selectedItems.length > 1}
                        setResetPasswordModal={setUserAccountForPasswordReset}
                      />
                    </Td>
                  )}
                  {canSeeActions && (
                    <Td>
                      <Checkbox
                        id={customer.id}
                        checked={selectedItems.map(({ id }) => id).includes(customer.id)}
                        onChange={(): void => {
                          handleSelectItem(customer)
                        }}
                      />
                    </Td>
                  )}
                  <TdId>
                    <IdWrapper>
                      {customer.id.substring(0, 8)}
                      <CopyToClipboard str={customer.id} />
                    </IdWrapper>
                  </TdId>
                  <TdId>
                    <IdWrapper>
                      {customer.userAccount.id.substring(0, 8)}
                      <CopyToClipboard str={customer.userAccount.id} />
                    </IdWrapper>
                  </TdId>
                  <Td>
                    <Status isActive={customer.isEnabled}>
                      {customer.isEnabled ? (
                        <FormattedMessage id="businessCustomer.active" defaultMessage="Active" />
                      ) : (
                        <FormattedMessage id="businessCustomer.inactive" defaultMessage="Inactive" />
                      )}
                    </Status>
                  </Td>
                  <Td>{`${customer.userAccount.firstName ?? ''} ${customer.userAccount.lastName ?? ''}`}</Td>
                  <Td>{customer.userAccount.phoneNumber?.number}</Td>
                  <Td>{customer.userAccount.email}</Td>
                  <Td>{customer.businessClient.name}</Td>
                  <Td>{customer.businessSite?.name || '-'}</Td>
                  <Td>{customer.kreditsSettings?.category ? intToLetter(customer.kreditsSettings?.category) : '-'}</Td>
                  <Td>
                    {customer.kreditsSettings?.amount ? (
                      <FormattedPrice value={customer.kreditsSettings?.amount} />
                    ) : (
                      '-'
                    )}
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
      {userAccountForPasswordReset && (
        <SucessResetPasswordRequestModal
          userAccountEmail={userAccountForPasswordReset.email as string}
          onClose={(): void => setUserAccountForPasswordReset(null)}
        />
      )}
    </div>
  )
}

export default BusinessCustomerList
